/** @jsxImportSource @emotion/react */

import { AlertTriangleFilled, Check } from "@imwebme/clay-icons";
import { vars } from "@imwebme/clay-token";
import { useEffect, useRef } from "react";
import { P, match } from "ts-pattern";
import type { NumberBadgeProps } from "../Badge";
import { DotBadge, NumberBadge } from "../Badge";
import { Flex } from "../utilities";
import { useTabContext } from "./Tab.provider";
import { getTabARIA } from "./Tab.utils";
import type { TabProps } from "./Tabs.types";

export const Tab = (props: TabProps) => {
  const tabContext = useTabContext();
  const tabRef = useRef<HTMLButtonElement>(null);
  const attr = getTabARIA({
    index: props._index,
    uniqueId: tabContext.uniqueId,
  });

  const isActive = tabContext.activeIndex === props._index;

  const numberBadgeVariantMap = match(tabContext.variant)
    .returnType<NumberBadgeProps["variant"]>()
    .with("filled", () => (isActive ? "primary-inverse" : "secondary"))
    .with("text", () => (isActive ? "primary" : "secondary"))
    .otherwise(() => "secondary");

  const numberBadgeSizeMap = match(tabContext.size)
    .returnType<NumberBadgeProps["size"]>()
    .with("small", () => "small")
    .with("medium", () => "medium")
    .otherwise(() => "large");

  const renderContent = match(props)
    .with({ badge: P.union(P.string, P.number) }, ({ badge }) => (
      <>
        {props.icon}
        <Flex gap={vars.spacing[1]} alignItems="center">
          {props.text}

          <NumberBadge variant={numberBadgeVariantMap} size={numberBadgeSizeMap} number={badge} />
        </Flex>
      </>
    ))
    .with({ dot: P.boolean }, () => (
      <>
        {props.icon}
        <Flex
          gap={vars.spacing[1]}
          native={{
            // dot 일 경우 indicator 가 dot 영역까지 차지하면 안되기 때문에 넣어준 data attribute
            // plz check Tab.list.tsx for more detail
            "data-clay-dot-badge": true,
          }}
        >
          {props.text}

          <DotBadge variant="accent" sx={{ alignSelf: "start" }} />
        </Flex>
      </>
    ))
    .otherwise(() => (
      <>
        {props.icon}
        {props.text}
      </>
    ));

  const renderStatus = match(props.status)
    .with("success", () => <Check style={{ color: vars.semantic.color.iconSuccess }} />)
    .with("warning", () => (
      <AlertTriangleFilled style={{ color: vars.semantic.color.iconCritical }} />
    ))
    .otherwise(() => null);

  useEffect(() => {
    if (isActive) {
      tabRef.current?.focus();
    }
  }, [isActive]);

  return (
    <button
      ref={tabRef}
      type="button"
      onClick={props._onClick}
      role="tab"
      tabIndex={-1}
      aria-selected={isActive}
      aria-disabled={props.disabled}
      data-selected={isActive}
      disabled={props.disabled}
      {...attr.tab}
      css={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: props.disabled ? "not-allowed" : "pointer",
        gap: vars.spacing[2],
      }}
    >
      {renderContent}

      {renderStatus}
    </button>
  );
};
